import React, { useState, useEffect } from 'react';
import { ArrowRight, Shield, Zap, Users, TrendingUp, Award, ChevronRight, Play, Star, CheckCircle, BarChart3, Hammer, Truck, Calendar, Bell, Settings, Home, Package, FileText, PieChart, Wrench, Building, HardHat, Layers, Clock, Target, Lightbulb, Cpu, Activity, Globe } from 'lucide-react';
import {useNavigate} from "react-router-dom";
import '../Styles/home.css';

const UnitechHomePage = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentUpdate, setCurrentUpdate] = useState(0);
    const [time, setTime] = useState(new Date());

    useEffect(() => {
        setIsVisible(true);
        const updateInterval = setInterval(() => {
            setCurrentUpdate(prev => (prev + 1) % 3);
        }, 5000);

        const timeInterval = setInterval(() => {
            setTime(new Date());
        }, 1000);

        return () => {
            clearInterval(updateInterval);
            clearInterval(timeInterval);
        };
    }, []);

    const companyUpdates = [
        {
            title: "Major Contract Secured - €15M Marina Bay Project",
            content: "We are pleased to announce the successful acquisition of a significant commercial contract worth €15 million for the Marina Bay development project, strengthening our market position in premium construction materials.",
            type: "Commercial Success",
            time: "2 hours ago",
            priority: "High"
        },
        {
            title: "Mandatory Safety Training - Compliance Update",
            content: "All operational personnel are required to complete the updated safety certification training by Friday. This initiative ensures full compliance with industry regulations and maintains our safety excellence standards.",
            type: "Operations",
            time: "4 hours ago",
            priority: "Critical"
        },
        {
            title: "Premium Concrete Inventory - Quality Enhancement",
            content: "New shipment of high-performance concrete materials now available. Enhanced specifications meet the most demanding project requirements with superior durability and performance characteristics.",
            type: "Inventory Management",
            time: "6 hours ago",
            priority: "Medium"
        }
    ];
    const navigate = useNavigate();


    return (
        <>

            <div className="main-container">
                {/* Background Effects */}
                <div className="bg-pattern"></div>
                <div className="grid-overlay"></div>
                <div className="floating-elements">
                    <div className="floating-orb floating-orb-1"></div>
                    <div className="floating-orb floating-orb-2"></div>
                    <div className="floating-orb floating-orb-3"></div>
                    <div className="floating-orb floating-orb-4"></div>
                    <div className="floating-orb floating-orb-5"></div>
                </div>

                {/* Navigation */}
                <nav className="navbar">
                    <div className="nav-container">
                        <div className="nav-brand">
                            <div className="nav-logo">
                                <Building style={{ width: '24px', height: '24px', color: 'white' }} />
                            </div>
                            <div>
                                <h1 className="nav-title">UNITECH IKK</h1>
                                <p className="nav-subtitle">Building & Construction Materials Solutions</p>
                            </div>
                        </div>

                        <div className="nav-actions">
                            <div className="nav-time">
                                <div className="nav-time-main">
                                    {time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                <div className="nav-time-sub">
                                    {time.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                                </div>
                            </div>

                            <button className="nav-bell">
                                <Bell style={{ width: '16px', height: '16px' }} />
                                <span className="nav-bell-badge">3</span>
                            </button>

                            <button
                                className="primary-button"
                                onClick={() => navigate('/login')}
                            >
                                Access System
                                <ArrowRight style={{ width: '16px', height: '16px' }} />
                            </button>
                        </div>
                    </div>
                </nav>

                {/* Hero Section */}
                <section className="hero">
                    <div className="hero-container">
                        <div className="hero-badge">
                            <Activity style={{ width: '16px', height: '16px', color: '#1e40af' }} />
                            <span className="hero-badge-text">Employee Portal Dashboard</span>
                        </div>

                        <h1 className="hero-title">
                            Welcome to the
                            <span className="hero-company">UNITECH IKK</span>
                            Management Platform
                        </h1>

                        <p className="hero-description">
                            Your comprehensive hub for construction materials management, project oversight,
                            and operational excellence. Access all enterprise tools and real-time business intelligence
                            from a single, integrated platform.
                        </p>

                        <div className="hero-stats">
                            <div className="stat-item">
                                <div className="stat-icon">
                                    <CheckCircle style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <div className="stat-content">
                                    <h3>System Operational</h3>
                                    <p>All services running optimally</p>
                                </div>
                            </div>

                            <div className="stat-item">
                                <div className="stat-icon">
                                    <Users style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <div className="stat-content">
                                    <h3>247 Users Online</h3>
                                    <p>Currently active employees</p>
                                </div>
                            </div>

                            <div className="stat-item">
                                <div className="stat-icon">
                                    <Globe style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <div className="stat-content">
                                    <h3>Multi-Site Access</h3>
                                    <p>Connected across all locations</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Performance Metrics */}
                <section className="section">
                    <div className="section-container">
                        <div className="section-header">
                            <h2 className="section-title">Real-Time Performance Analytics</h2>
                            <p className="section-subtitle">
                                Key performance indicators and operational metrics for informed decision-making
                            </p>
                        </div>

                        <div className="metrics-grid">
                            {[
                                { number: '847', label: 'Active Projects', icon: Hammer, trend: 12, bg: 'linear-gradient(135deg, #1e40af, #0ea5e9)' },
                                { number: '€15.2M', label: 'Monthly Revenue', icon: Target, trend: 8, bg: 'linear-gradient(135deg, #059669, #0d9488)' },
                                { number: '89%', label: 'Delivery Rate', icon: Truck, trend: 5, bg: 'linear-gradient(135deg, #7c3aed, #a855f7)' },
                                { number: '99.7%', label: 'Client Satisfaction', icon: Star, trend: 2, bg: 'linear-gradient(135deg, #ea580c, #f97316)' }
                            ].map((metric, index) => (
                                <div key={index} className="metric-card">
                                    <div className="metric-header">
                                        <div className="metric-icon" style={{ background: metric.bg }}>
                                            <metric.icon style={{ width: '20px', height: '20px', color: 'white' }} />
                                        </div>
                                        <div className="metric-trend">
                                            <TrendingUp style={{ width: '14px', height: '14px' }} />
                                            +{metric.trend}%
                                        </div>
                                    </div>
                                    <div className="metric-number">{metric.number}</div>
                                    <div className="metric-label">{metric.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Quick Access Tools */}
                <section className="section">
                    <div className="section-container">
                        <div className="section-header">
                            <h2 className="section-title">Enterprise Applications</h2>
                            <p className="section-subtitle">
                                Access your daily workflow tools and operational systems
                            </p>
                        </div>

                        <div className="access-grid">
                            {[
                                {
                                    icon: Package,
                                    title: 'Inventory Management System',
                                    description: 'Real-time stock monitoring, automated reordering, supplier management, and comprehensive materials tracking across all project sites and warehouses.',
                                    bg: 'linear-gradient(135deg, #1e40af, #0ea5e9)'
                                },
                                {
                                    icon: FileText,
                                    title: 'Project Documentation',
                                    description: 'Centralized project reports, compliance documentation, progress tracking, and collaborative workspace for all construction project deliverables.',
                                    bg: 'linear-gradient(135deg, #059669, #0d9488)'
                                },
                                {
                                    icon: Calendar,
                                    title: 'Resource Planning',
                                    description: 'Strategic resource allocation, team scheduling, equipment management, and cross-departmental coordination for optimal operational efficiency.',
                                    bg: 'linear-gradient(135deg, #7c3aed, #a855f7)'
                                }
                            ].map((card, index) => (
                                <div key={index} className="access-card">
                                    <div className="access-icon-container" style={{ background: card.bg }}>
                                        <card.icon style={{ width: '28px', height: '28px', color: 'white' }} />
                                    </div>

                                    <h3 className="access-title">{card.title}</h3>
                                    <p className="access-description">{card.description}</p>

                                    <div className="access-link">
                                        <span>Launch Application</span>
                                        <ChevronRight style={{ width: '16px', height: '16px' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Company Updates */}
                <section className="section">
                    <div className="section-container">
                        <div className="section-header">
                            <h2 className="section-title">Corporate Communications</h2>
                            <p className="section-subtitle">
                                Latest company announcements and operational updates
                            </p>
                        </div>

                        <div className="news-card">
                            <div className="news-content">
                                <div className="news-icon">
                                    <Bell style={{ width: '20px', height: '20px', color: 'white' }} />
                                </div>
                                <div className="news-text">
                                    <div className="news-header">
                                        <h3 className="news-title">{companyUpdates[currentUpdate].title}</h3>
                                        <div className="news-meta">
                                            <span className="news-time">{companyUpdates[currentUpdate].time}</span>
                                            <span className={`news-priority priority-${companyUpdates[currentUpdate].priority.toLowerCase()}`}>
                        {companyUpdates[currentUpdate].priority}
                      </span>
                                        </div>
                                    </div>

                                    <div className="news-type">{companyUpdates[currentUpdate].type}</div>
                                    <p className="news-description">{companyUpdates[currentUpdate].content}</p>
                                </div>
                            </div>

                            <div className="news-indicators">
                                {companyUpdates.map((_, index) => (
                                    <div
                                        key={index}
                                        className={`news-dot ${index === currentUpdate ? 'active' : ''}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Call to Action */}
                <section className="section cta-section">
                    <div className="section-container">
                        <div className="cta-card">
                            <div className="cta-icon">
                                <Zap style={{ width: '40px', height: '40px', color: 'white' }} />
                            </div>

                            <h2 className="cta-title">Ready to Begin Your Workday?</h2>

                            <p className="cta-description">
                                Access your personalized dashboard with real-time project data,
                                team communications, and enterprise-grade productivity tools.
                            </p>

                            <button
                                className="cta-button"
                                onClick={() => alert('Redirecting to Executive Dashboard')}
                            >
                                Launch Executive Dashboard
                                <ArrowRight style={{ width: '20px', height: '20px' }} />
                            </button>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="footer">
                    <div className="footer-container">
                        <div className="footer-brand">
                            <div className="footer-logo">
                                <Building style={{ width: '20px', height: '20px', color: 'white' }} />
                            </div>
                            <div className="footer-text">
                                <h3>UNITECH IKK - IKK GROUP</h3>
                                <p>Building & Construction Materials Solutions</p>
                            </div>
                        </div>

                        <div className="footer-info">
                            <span className="footer-contact">IT Support: Extension 2847</span>
                            <span className="footer-contact">Human Resources: Extension 2156</span>
                            <div className="footer-status">
                                <span className="status-indicator"></span>
                                <span>All Systems Operational</span>
                            </div>
                        </div>
                    </div>
                </footer>
            </div>
        </>
    );
};

export default UnitechHomePage;